import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Zap, Tag, LayoutGrid } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import BrandLogo, { matchBrandSlug } from "@/components/BrandLogo";
import CTASection from "@/components/CTASection";
import { CatalogCard } from "@/pages/Products";
import { CATALOG, CATEGORIES } from "@/data/catalog";
import { REGIONS, REGION_LINKS } from "@/data/regions";
import { useSEO } from "@/hooks/useSEO";

export default function RegionPage() {
  const { slug } = useParams();
  const region = REGIONS[slug];
  useSEO(
    region ? `${region.titleA} ${region.titleB} — Vowpin Global` : "Vowpin Global",
    region ? region.sub : "Digital gift card distribution by Vowpin Global."
  );
  if (!region) return <Navigate to="/products" replace />;

  const items = CATALOG.filter((p) => p.region === region.code);
  const logoSlugs = [...new Set(items.map((p) => matchBrandSlug(p.name)).filter(Boolean))].slice(0, 18);
  const groups = CATEGORIES.filter((c) => c.key !== "all")
    .map((c) => ({ ...c, items: items.filter((p) => p.category === c.key) }))
    .filter((g) => g.items.length > 0);

  const highlights = [
    { icon: LayoutGrid, value: `${items.length}`, label: "Products live" },
    { icon: MapPin, value: region.code, label: "Redeemable nationwide" },
    { icon: Zap, value: "Instant", label: "Digital delivery" },
    { icon: Tag, value: "Volume", label: "Best-offer pricing" },
  ];

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#f4a900]/40 bg-[#f4a900]/[0.08] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b87e00]">
              <MapPin className="h-3.5 w-3.5" />
              Region · {region.name}
            </p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              {region.titleA} <span className="text-gradient-blue">{region.titleB}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              {region.sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="region-talk-to-sales-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-8 py-4 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4] hover:shadow-[0_14px_36px_-8px_rgba(8,22,168,0.6)]"
              >
                Talk to Sales
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="region-request-catalogue-btn"
                className="rounded-full border border-[#d7dce8] bg-white px-8 py-4 text-sm font-semibold text-[#374151] transition-colors duration-300 hover:border-[#0816a8]/50 hover:text-[#0816a8]"
              >
                Request {region.name === "India" ? "India" : region.name.replace(/^the /, "")} Catalogue
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="region-switcher">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9aa3b5]">Regions:</span>
              {REGION_LINKS.map((r) => (
                <Link
                  key={r.slug}
                  to={r.to}
                  data-testid={`region-switch-${r.slug}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 ${
                    r.slug === slug
                      ? "bg-[#0816a8] text-white"
                      : "border border-[#e5e9f2] bg-white text-[#64748b] hover:border-[#0816a8]/40 hover:text-[#0816a8]"
                  }`}
                >
                  {r.label.replace(" Catalogue", "")}
                </Link>
              ))}
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {highlights.map((h, i) => (
              <Reveal key={h.label} delay={i * 0.07}>
                <div className="glow-card flex items-center gap-4 rounded-[20px] p-5" data-testid={`region-highlight-${i}`}>
                  <h.icon className="h-6 w-6 shrink-0 text-[#0816a8]" />
                  <div>
                    <p className="font-display text-xl font-semibold text-[#111827]">{h.value}</p>
                    <p className="text-xs text-[#64748b]">{h.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured brands */}
      {logoSlugs.length > 0 && (
        <section className="border-y border-[#e8ecf4] bg-[#f7f9fc] py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <SectionHeading
              eyebrow="Featured Brands"
              title="Instantly giftable, locally loved"
              sub={`A snapshot of the ${region.name} catalog — the full list with denominations is available on request.`}
            />
            <div className="mt-14 grid grid-cols-3 gap-4 sm:grid-cols-5 lg:grid-cols-6">
              {logoSlugs.map((s, i) => (
                <Reveal key={s} delay={i * 0.04}>
                  <BrandLogo slug={s} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full region catalog, grouped */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Live Inventory"
            title={`The ${region.name.replace(/^the /, "")} Catalog`}
            sub={`${items.length} products in active distribution — pricing tailored to your volume.`}
          />
          {groups.map((g) => (
            <div key={g.key} className="mt-16" data-testid={`region-group-${g.key}`}>
              <Reveal>
                <div className="flex items-baseline gap-4">
                  <h3 className="font-display text-2xl font-semibold text-[#111827]">{g.label}</h3>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9aa3b5]">
                    {g.items.length} products
                  </span>
                </div>
              </Reveal>
              <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {g.items.map((item) => (
                  <CatalogCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection title={region.cta} sub={region.ctaSub} />
    </main>
  );
}
